// Sprint 1 — Onboarding post-registro
import { CalendarDays, Repeat } from "lucide-react";
import { css, cx } from "styled-system/css";
import { cf } from "@/components/brand/sfturnos-styles";
import { useOnboardingWizard } from "@/context/onboarding-wizard-context";
import { onboardingChoiceCard } from "../onboarding-ui";

const DAY_LABELS = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
const DAY_SHORT = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

export function OnboardingStepDays() {
  const { state, dispatch } = useOnboardingWizard();
  const enabledCount = state.days.filter((d) => d.enabled).length;
  const canContinue = enabledCount >= 1;

  function toggleDay(day: number) {
    const d = state.days.find((x) => x.day === day);
    if (!d) return;
    dispatch({ type: "SET_DAY_CONFIG", day, config: { enabled: !d.enabled } });
  }

  return (
    <div>
      <h1 className={css({ fontSize: "2xl", fontWeight: "bold", color: "#0f172a", mb: "2" })}>Días de atención</h1>
      <p className={css({ fontSize: "sm", color: "#64748b", mb: "8", maxW: "42rem", lineHeight: 1.6 })}>
        Definí cómo se organiza la disponibilidad de tu agenda: por días de la semana o por fechas puntuales.
      </p>

      <p className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a", mb: "3" })}>Tipo de disponibilidad</p>
      <div className={css({ display: "grid", gap: "4", mb: "8" })}>
        {/* Modo repetitivo — único modo activo hoy; incluye la selección de días como continuación natural */}
        <div className={cx(onboardingChoiceCard({ selected: true, wide: true }), css({ cursor: "default" }))}>
          <Repeat className={css({ w: "7", h: "7", color: "token(colors.brand.500)" })} strokeWidth={1.75} />
          <span className={css({ fontWeight: "semibold", color: "#0f172a" })}>Modo repetitivo</span>
          <span className={css({ fontSize: "sm", color: "#64748b", lineHeight: 1.5 })}>
            Para negocios con el mismo horario todas las semanas (ej: una cancha que abre de lunes a viernes de 9 a
            21). Los días y horarios que definas se repiten automáticamente cada semana.
          </span>

          <div
            className={css({
              mt: "2",
              pt: "4",
              borderTopWidth: "1px",
              borderColor: "#e2e8f0",
            })}
          >
            <p className={css({ fontSize: "sm", fontWeight: "medium", color: "#334155", mb: "3" })}>
              Elegí los días que atendés
            </p>
            <div className={css({ display: "flex", flexWrap: "wrap", gap: "2" })}>
              {state.days.map((d) => (
                <button
                  key={d.day}
                  type="button"
                  aria-pressed={d.enabled}
                  aria-label={DAY_LABELS[d.day]}
                  onClick={() => toggleDay(d.day)}
                  className={css({
                    px: "4",
                    py: "2",
                    rounded: "full",
                    fontSize: "sm",
                    fontWeight: "medium",
                    cursor: "pointer",
                    borderWidth: "1.5px",
                    borderColor: d.enabled ? "token(colors.brand.500)" : "#e2e8f0",
                    bg: d.enabled ? "token(colors.brand.50)" : "white",
                    color: d.enabled ? "token(colors.brand.700)" : "#64748b",
                    transition: "border-color 0.15s, background 0.15s, color 0.15s, transform 0.1s ease-out",
                    _active: { transform: "scale(0.96)" },
                  })}
                >
                  {DAY_SHORT[d.day]}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Fechas específicas — deshabilitado por ahora */}
        <div
          className={cx(
            onboardingChoiceCard({ selected: false, wide: true }),
            css({ opacity: 0.6, cursor: "not-allowed" }),
          )}
        >
          <div className={css({ display: "flex", alignItems: "center", gap: "3" })}>
            <CalendarDays className={css({ w: "7", h: "7", color: "#94a3b8", flexShrink: 0 })} strokeWidth={1.75} />
            <span className={css({ fontWeight: "semibold", color: "#64748b" })}>Fechas específicas</span>
            <span
              className={css({
                fontSize: "xs",
                fontWeight: "semibold",
                color: "#94a3b8",
                bg: "#f1f5f9",
                px: "2.5",
                py: "1",
                rounded: "full",
              })}
            >
              Próximamente
            </span>
          </div>
          <span className={css({ fontSize: "sm", color: "#94a3b8", lineHeight: 1.5 })}>
            Para negocios con disponibilidad que cambia semana a semana o que solo atienden en fechas puntuales (ej:
            eventos, turnos por temporada). Vas a poder elegir fecha por fecha en vez de por día de la semana.
          </span>
        </div>
      </div>

      {canContinue ? (
        <button
          type="button"
          onClick={() => dispatch({ type: "NEXT_STEP" })}
          className={cx(
            cf.linkPrimaryOnDark,
            css({
              px: "6",
              py: "3",
              border: "none",
              fontSize: "sm",
              cursor: "pointer",
              bg: "linear-gradient(90deg, token(colors.brand.600), token(colors.brand.500))",
            }),
          )}
        >
          Continuar
        </button>
      ) : null}
    </div>
  );
}
